import { Injectable } from '@angular/core';
import { ILanguageProvider } from '@modeso/aumico-internationalization';
import { Constants, LANGUAGES } from '@modeso/aumico-lib-core-fe';
import { IUserPreferences } from '@modeso/aumico-lib-users-fe';
import { environment } from '../../../../aumico-project-reports/src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageService implements ILanguageProvider {
    private readonly currentLanguageKey = 'currentLanguage';
    private readonly currentUserEmail = 'currentUserEmail';
    private readonly currentOrganizationName = 'currentOrganizationName';
    private readonly userPreferences = 'userPreferences';
    private readonly sideNavCollapsedKey = Constants.SIDE_NAV_STATE_COLLAPSED_KEY;

    public setCurrentLanguage(lang: string): void {
        let language = lang;
        if (lang == null) {
            language = environment.defaultLanguage;
        }
        localStorage.setItem(this.currentLanguageKey, language);
    }

    public getCurrentLanguage(): LANGUAGES {
        const currentLanguage = localStorage.getItem(this.currentLanguageKey) as LANGUAGES;
        if (currentLanguage == null) {
            return environment.defaultLanguage;
        }
        return currentLanguage;
    }

    public getCurrentLanguageWithoutFallback(): string | null {
        return localStorage.getItem(this.currentLanguageKey);
    }

    public getBrowserLanguage(): string | null {
        const browserLang = navigator.language || (navigator as any).userLanguage;
        return browserLang ? browserLang.toLowerCase() : null;
    }

    public setCurrentUserEmail(userEmail: string): void {
        localStorage.setItem(this.currentUserEmail, userEmail);
    }

    public getCurrentUserEmail(): string | undefined {
        return localStorage.getItem(this.currentUserEmail);
    }

    public setCurrentOrganizationName(organizationName: string): void {
        localStorage.setItem(this.currentOrganizationName, organizationName);
    }

    public getCurrentOrganizationName(): string | undefined {
        return localStorage.getItem(this.currentOrganizationName);
    }

    public setUserPrefrences(userPreferences: IUserPreferences): void {
        localStorage.setItem(this.userPreferences, JSON.stringify(userPreferences));
    }

    public getUserPrefrences(): IUserPreferences | undefined {
        return JSON.parse(localStorage.getItem(this.userPreferences));
    }

    public clear(): void {
        localStorage.clear();
    }

    public clearAllExceptLanguage(): void {
        const currentLanguage = this.getCurrentLanguage();
        this.clear();
        if (currentLanguage != null) {
            localStorage.setItem(this.currentLanguageKey, currentLanguage);
        }
    }

    isKeyInLocalStorage(key: string): boolean {
        return localStorage.getItem(key) !== null;
    }

    getItem(key: string): string {
        return localStorage.getItem(key);
    }

    removeItem(key: string): void {
        localStorage.removeItem(key);
    }

    setSideNavCollapsedKey(value: '0' | '1') {
        localStorage.setItem(this.sideNavCollapsedKey, value);
    }
}
